import { PAUSE_BUDGET_THRESHOLD, type BudgetInputs, type EntityCategory, type RecommendationRow } from './types';

/**
 * How one category's share of the target was split between the rows that
 * are NOT being changed and the rows that are.
 */
export interface CategoryAllocation {
  /** This category's share of the target daily budget. */
  share: number;
  /**
   * Current daily budgets of the unchecked active rows in this category.
   * They stay where they are, so this is spoken for before anything is
   * scaled. Unassigned rows count here under DR.
   */
  committed: number;
  /** max(share − committed, 0): what the checked rows are scaled to fit. */
  remaining: number;
  /** Σ raw (performance-based) recommendations of the checked rows. */
  rawSum: number;
  /** min(1, remaining / rawSum); 1 when there is nothing to scale down. */
  scale: number;
  checkedCount: number;
  /** Unchecked ACTIVE rows — the ones that commit budget. */
  uncheckedCount: number;
}

/** The allocation pool, in the terms the totals bar and the review show. */
export interface AllocationSummary {
  targetDailyBudget: number;
  brand: CategoryAllocation;
  dr: CategoryAllocation;
  /** Σ current budgets of every unchecked active row (both categories, unassigned included). */
  committed: number;
  uncheckedCount: number;
  /** The part of `committed` that comes from rows with no initiative type. */
  unassignedCommitted: number;
  unassignedCount: number;
  checkedCount: number;
  /** Σ adjusted budgets of the checked rows after caps and the pause threshold. */
  checkedAdjusted: number;
  /** committed + checkedAdjusted: the daily total once the checked rows are applied. */
  totalAfterApproval: number;
  /** targetDailyBudget − totalAfterApproval. Positive = target headroom left unused. */
  headroom: number;
  /**
   * Some category's unchecked rows alone meet or exceed its share while
   * checked rows remain to scale — those rows scale toward $0.
   */
  overCommitted: boolean;
  /** Categorized rows held by a pin or the brand-search policy (always unchecked; active ones are in `committed`). */
  lockedCount: number;
  /** Checked rows the step cap clamped. */
  stepCappedCount: number;
  /**
   * Σ (clamped − unclamped) over the step-capped rows: positive = the cap
   * kept more budget than the lane scale gave (shows up as less headroom or
   * as over-commitment), negative = it held back increases.
   */
  stepCapAdjustment: number;
}

export interface AllocationResult {
  /** Shallow copies of the input rows with adjustedBudget / percentChange / shouldPause / pausedByScaling set. */
  rows: RecommendationRow[];
  allocation: AllocationSummary;
}

type Targets = Pick<BudgetInputs, 'targetDailyBudget' | 'targetDRRoas' | 'targetBrandRoas'>;

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Scale the raw recommendations to the target daily budget, treating every
 * UNCHECKED active row as a commitment at its current budget.
 *
 * The pool: each category's share of the target is reduced by the current
 * budgets of its unchecked active rows (nobody inherits the spend of a row
 * that is staying put), and the checked rows are scaled down — never up —
 * to fit what remains. Unassigned (no initiative type) active rows commit
 * under DR. Inactive rows (paused, or no spend in their window) commit
 * nothing: they are not spending, so counting their budget would overstate
 * the pool.
 *
 * Unchecked rows come back at their current budget (0% change, never a
 * pause). A LOCKED row (`row.lock`: a saved pin or the brand-search policy,
 * see applyRowControls) is unchecked whatever `excludedIds` says. Checked
 * rows get the existing caps: never above the raw performance
 * recommendation, never above current when under the ROAS target. Then the
 * step cap (`row.stepCapPct`, when set) clamps a checked row with a current
 * budget to current × (1 ± cap), and only then the pause rule runs: a pause
 * below the threshold (missing-data rows excepted). So a capped row can only
 * be paused when current × (1 − cap) is under the threshold.
 *
 * Pure: input rows are not mutated.
 */
export function allocateBudgets(
  rows: readonly RecommendationRow[],
  targets: Targets,
  brandSpendPct: number,
  excludedIds: ReadonlySet<string>,
): AllocationResult {
  const out = rows.map((r) => ({ ...r }));
  const assigned = out.filter((r) => r.category !== null);
  const isChecked = (r: RecommendationRow) => !excludedIds.has(r.entityId) && !r.lock;

  const committedIn = (category: EntityCategory) =>
    assigned.filter((r) => r.category === category && r.isActive && !isChecked(r)).reduce((s, r) => s + r.currentDailyBudget, 0);
  const uncheckedCountIn = (category: EntityCategory) =>
    assigned.filter((r) => r.category === category && r.isActive && !isChecked(r)).length;

  // Unassigned rows are active spend with no home: they sit in the DR
  // share (the larger, default category) so the total still adds up. The
  // review step is gated while any active row is unassigned; when the user
  // overrides that gate, this carve-out is what gets applied — the rows
  // keep their current budget and DR rows scale to what is left.
  const unassignedActive = out.filter((r) => r.category === null && r.isActive);
  const unassignedCommitted = unassignedActive.reduce((s, r) => s + r.currentDailyBudget, 0);

  const categoryAllocation = (category: EntityCategory, share: number, extraCommitted: number): CategoryAllocation => {
    const committed = committedIn(category) + extraCommitted;
    const remaining = Math.max(share - committed, 0);
    const checked = assigned.filter((r) => r.category === category && isChecked(r));
    const rawSum = checked.reduce((s, r) => s + r.recommendedBudget, 0);
    const scale = rawSum > remaining ? remaining / rawSum : 1;
    return {
      share,
      committed,
      remaining,
      rawSum,
      scale,
      checkedCount: checked.length,
      uncheckedCount: uncheckedCountIn(category),
    };
  };

  const brand = categoryAllocation('BRAND', targets.targetDailyBudget * brandSpendPct, 0);
  const dr = categoryAllocation('DR', targets.targetDailyBudget * (1 - brandSpendPct), unassignedCommitted);

  let stepCappedCount = 0;
  let stepCapAdjustment = 0;
  for (const row of assigned) {
    delete row.stepCapped;
    delete row.unclampedBudget;
    if (!isChecked(row)) {
      row.adjustedBudget = row.currentDailyBudget;
      row.percentChange = 0;
      row.shouldPause = false;
      row.pausedByScaling = false;
      continue;
    }
    const { scale } = row.category === 'BRAND' ? brand : dr;
    const targetRoas = row.category === 'BRAND' ? targets.targetBrandRoas : targets.targetDRRoas;
    const activeRoas = row.category === 'BRAND' ? row.brandRoas : row.drRoas;
    const capped = (budget: number) => {
      // Performance cap: never recommend higher than the raw performance-based budget
      let b = Math.min(budget, row.performanceBudget);
      // Underperformer cap: if ROAS < target, also never exceed current budget
      if (activeRoas < targetRoas && row.currentDailyBudget > 0) {
        b = Math.min(b, row.currentDailyBudget);
      }
      return round2(b);
    };

    let adjusted = capped(round2(row.recommendedBudget * scale));
    // Step cap: after the lane scale and the caps, before the pause rule. A
    // row with no current budget has nothing to step from and is left alone.
    const cap = row.stepCapPct;
    if (cap !== undefined && row.currentDailyBudget > 0) {
      const floor = round2(row.currentDailyBudget * (1 - cap));
      const ceiling = round2(row.currentDailyBudget * (1 + cap));
      const clamped = Math.min(Math.max(adjusted, floor), ceiling);
      if (clamped !== adjusted) {
        row.stepCapped = true;
        row.unclampedBudget = adjusted;
        adjusted = clamped;
      }
    }
    row.adjustedBudget = adjusted;
    row.percentChange = row.currentDailyBudget > 0 ? ((row.adjustedBudget - row.currentDailyBudget) / row.currentDailyBudget) * 100 : 0;

    // Pause threshold. CRITICAL: never pause on missing data — a $0
    // recommendation caused by absent modeled revenue (pipeline lag /
    // attribution gap) is a data hole, not a signal to pause a live,
    // spending campaign.
    if (row.adjustedBudget < PAUSE_BUDGET_THRESHOLD && !row.hasMissingData) {
      row.shouldPause = true;
      // A pool that leaves the checked rows only a few dollars pauses them
      // without being over-committed; the review asks for an acknowledgment
      // on these the same way.
      row.pausedByScaling = capped(row.recommendedBudget) >= PAUSE_BUDGET_THRESHOLD;
      row.adjustedBudget = 0;
      row.percentChange = row.currentDailyBudget > 0 ? -100 : 0;
      // The pause decides the row's outcome, not the clamp.
      delete row.stepCapped;
      delete row.unclampedBudget;
    } else {
      row.shouldPause = false;
      row.pausedByScaling = false;
    }
    if (row.stepCapped) {
      stepCappedCount += 1;
      stepCapAdjustment += row.adjustedBudget - (row.unclampedBudget ?? row.adjustedBudget);
    }
  }

  const checkedRows = assigned.filter(isChecked);
  const checkedAdjusted = checkedRows.reduce((s, r) => s + r.adjustedBudget, 0);
  const committed = brand.committed + dr.committed;
  const totalAfterApproval = committed + checkedAdjusted;
  const overCommitted = [brand, dr].some((c) => c.checkedCount > 0 && c.rawSum > 0 && c.remaining <= 0);

  return {
    rows: out,
    allocation: {
      targetDailyBudget: targets.targetDailyBudget,
      brand,
      dr,
      committed,
      uncheckedCount: brand.uncheckedCount + dr.uncheckedCount + unassignedActive.length,
      unassignedCommitted,
      unassignedCount: unassignedActive.length,
      checkedCount: checkedRows.length,
      checkedAdjusted,
      totalAfterApproval,
      headroom: targets.targetDailyBudget - totalAfterApproval,
      overCommitted,
      lockedCount: assigned.filter((r) => r.lock).length,
      stepCappedCount,
      stepCapAdjustment: round2(stepCapAdjustment),
    },
  };
}

export interface PoolTotals {
  /** Σ current budgets of the unchecked active rows (what stays as is). */
  committed: number;
  uncheckedCount: number;
  /** Σ budgets the checked rows will be set to — hand edits included, pauses as $0. */
  checkedNew: number;
  checkedCount: number;
  /** committed + checkedNew */
  totalAfterApproval: number;
  /** target − totalAfterApproval */
  headroom: number;
}

/**
 * The pool as the user will see it applied: the hook's allocation does not
 * know about hand-edited budgets, so the totals bar and the review fold
 * them in here. Rows without a category are never checked: an active one
 * stays at its current budget (committed, matching the allocation's DR
 * carve-out), whether the initiative-type gate still holds the review back
 * or the user approved past it. A locked row counts as unchecked whatever
 * `excludedIds` says, as in allocateBudgets.
 */
export function poolTotals(
  rows: readonly RecommendationRow[],
  excludedIds: ReadonlySet<string>,
  editOverrides: ReadonlyMap<string, number>,
  targetDailyBudget: number,
): PoolTotals {
  let committed = 0;
  let uncheckedCount = 0;
  let checkedNew = 0;
  let checkedCount = 0;
  for (const row of rows) {
    if (row.category === null) {
      if (row.isActive) {
        committed += row.currentDailyBudget;
        uncheckedCount += 1;
      }
      continue;
    }
    if (excludedIds.has(row.entityId) || row.lock) {
      if (row.isActive) {
        committed += row.currentDailyBudget;
        uncheckedCount += 1;
      }
      continue;
    }
    checkedCount += 1;
    if (!row.shouldPause) {
      checkedNew += editOverrides.get(row.entityId) ?? row.adjustedBudget;
    }
  }
  const totalAfterApproval = committed + checkedNew;
  return {
    committed,
    uncheckedCount,
    checkedNew,
    checkedCount,
    totalAfterApproval,
    headroom: targetDailyBudget - totalAfterApproval,
  };
}
